import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { queryClient } from "../../queries/client";
import { useCreateDevice, useUpdateDevice, useGetAllLocations } from "../../queries/admin";
import { useLocationStore } from "../../store/useLocationStore";
import DeviceImage from "./DeviceImage";
import { v4 as uuid } from "uuid";

export interface DeviceModel {
  deviceId: string;
  name: string;
  dataRate: number;
  imageKey: string;
  locationId: string;
}

const initialDeviceValues: DeviceModel = {
  deviceId: "",
  name: "",
  dataRate: 60,
  imageKey: "",
  locationId: "",
};

export default function AddDeviceToLocationModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [deviceModel, setDeviceModel] = useState<DeviceModel>(initialDeviceValues);
  const { mutate: createDevice, isPending } = useCreateDevice();
  const { data: locations, isLoading: locationsLoading } = useGetAllLocations();
  const { selectedLocationId } = useLocationStore();

  const handleSubmit = () => {
    if (!deviceModel.name.trim()) {
      toast("Device name is required", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (!deviceModel.locationId) {
      toast("Please select a location", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    if (!deviceModel.deviceId) {
      deviceModel.deviceId = uuid();
    }

    createDevice(deviceModel, {
      onSuccess: () => {
        toast("Device added successfully", {
          type: "success",
          autoClose: 2000,
        });

        queryClient.invalidateQueries({ queryKey: ["get-location-devices"] });
        setDeviceModel(initialDeviceValues);
        onClose();
      },
      onError: (error: any) => {
        toast(error?.response?.data?.errorMessage || "Failed to add device", {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setDeviceModel(initialDeviceValues);
    } else if (selectedLocationId) {
      setDeviceModel((prev) => ({ ...prev, locationId: selectedLocationId }));
    }
  }, [isOpen, selectedLocationId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!locationsLoading && locations && locations.length > 0 && !deviceModel.locationId) {
      const defaultLocationId = selectedLocationId || locations[0]?.id?.toString() || "";
      if (defaultLocationId) {
        setDeviceModel((prev) => ({
          ...prev,
          locationId: defaultLocationId,
        }));
      }
    }
  }, [isOpen, locationsLoading, locations, deviceModel.locationId, selectedLocationId]);

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white dark:bg-gray-800 px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Add Device
                  </h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure and add a new device to your location.
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Device name
                    </label>
                    <div className="mt-1.5">
                      <input
                        value={deviceModel.name}
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="off"
                        placeholder="Device name"
                        onChange={(e) => {
                          setDeviceModel({
                            ...deviceModel,
                            name: e.target.value,
                          });
                        }}
                        className="block w-full px-3 rounded-md border border-gray-300 dark:border-gray-600 py-2 text-gray-900 dark:text-white dark:bg-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="location"
                      className="block text-sm font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Location
                    </label>
                    <div className="mt-1.5 relative">
                      <select
                        id="location"
                        name="location"
                        value={deviceModel.locationId}
                        onChange={(e) => {
                          setDeviceModel({
                            ...deviceModel,
                            locationId: e.target.value,
                          });
                        }}
                        className="block w-full h-[38px] pl-3 pr-10 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 appearance-none bg-white text-gray-900 dark:text-gray-200 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="">Select a location</option>
                        {locationsLoading ? (
                          <option disabled>Loading locations...</option>
                        ) : (
                          locations?.map((location: any) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                        <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Only admins can change location.
                    </p>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Device image
                  </label>
                  <div className="mt-1.5">
                    <DeviceImage deviceModel={deviceModel} setDeviceModel={setDeviceModel} />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Supported formats: PNG, JPG, JPEG.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Max. size per file: 50MB
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    className="rounded-md cursor-pointer border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                      isPending ||
                      !deviceModel.name.trim() ||
                      !deviceModel.locationId
                    }
                    className="rounded-md cursor-pointer bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPending ? "Adding..." : "Add Device"}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
